import { Match } from '../types';
import { parseLigaMxJornadaNumber } from './matchdayService';
import type { StageLeaderboard } from './mundial/mundialStageRankingService';

export interface ScoringMatch {
  id?: string;
  score_a: number | null;
  score_b: number | null;
  team_a: string;
  team_b?: string | null;
  phase?: string;
  date?: Date;
  team_a_logo?: string | null;
  team_b_logo?: string | null;
  group?: string | null;
  match_type?: string;
  jornada?: number | null;
  league?: string;
}

export type PointReason = 'exact' | 'draw' | 'winner' | 'stage_goals_bonus';

export interface PredictionLike {
  user_id: string;
  match_id: string;
  predicted_score_a: number;
  predicted_score_b: number;
}

export interface PointBreakdownItem {
  id: string;
  match?: ScoringMatch;
  label: string;
  phase?: string;
  predictedScoreA: number;
  predictedScoreB: number;
  actualScoreA: number;
  actualScoreB: number;
  points: number;
  reason: PointReason;
  date?: Date;
}

const REASON_LABELS: Record<PointReason, string> = {
  exact: 'Marcador exacto',
  draw: 'Empate acertado',
  winner: 'Ganador correcto',
  stage_goals_bonus: 'Bonus goles de etapa',
};

const LIGA_MX_STAGE_BONUSES = ['cuartos', 'semifinal', 'final'];

const STAGE_LABELS: Record<string, string> = {
  cuartos: 'Cuartos de Final',
  semifinal: 'Semifinales',
  final: 'Final',
};

export function getPointReasonLabel(reason: PointReason): string {
  return REASON_LABELS[reason];
}

export function calculateMatchPoints(
  pred: Pick<PredictionLike, 'predicted_score_a' | 'predicted_score_b'>,
  match: Pick<Match, 'score_a' | 'score_b'>
): { points: number; reason: PointReason | null } {
  if (match.score_a === null || match.score_b === null) {
    return { points: 0, reason: null };
  }

  if (
    pred.predicted_score_a === match.score_a &&
    pred.predicted_score_b === match.score_b
  ) {
    return { points: 3, reason: 'exact' };
  }

  if (
    match.score_a === match.score_b &&
    pred.predicted_score_a === pred.predicted_score_b
  ) {
    return { points: 1, reason: 'draw' };
  }

  const matchResult = Math.sign(match.score_a - match.score_b);
  const predictionResult = Math.sign(
    pred.predicted_score_a - pred.predicted_score_b
  );

  if (matchResult === predictionResult && matchResult !== 0) {
    return { points: 1, reason: 'winner' };
  }

  return { points: 0, reason: null };
}

export function buildUserPointBreakdown(
  userId: string,
  predictions: PredictionLike[],
  matchesById: Record<string, ScoringMatch>,
  options?: { includeStageBonuses?: boolean }
): PointBreakdownItem[] {
  const includeStageBonuses = options?.includeStageBonuses ?? true;
  const userPreds = predictions.filter((p) => p.user_id === userId);
  const items: PointBreakdownItem[] = [];

  userPreds.forEach((pred) => {
    const match = matchesById[pred.match_id];
    if (!match || match.score_a === null || match.score_b === null) return;

    const { points, reason } = calculateMatchPoints(pred, match);
    if (points <= 0 || !reason) return;

    items.push({
      id: match.id ?? pred.match_id,
      match,
      label: `${match.team_a} vs ${match.team_b ?? '—'}`,
      phase: match.phase,
      predictedScoreA: pred.predicted_score_a,
      predictedScoreB: pred.predicted_score_b,
      actualScoreA: match.score_a,
      actualScoreB: match.score_b,
      points,
      reason,
      date: match.date,
    });
  });

  if (includeStageBonuses) {
    LIGA_MX_STAGE_BONUSES.forEach((etapa) => {
      const partidosEtapa = Object.values(matchesById).filter(
        (m) => m.phase && m.phase.toLowerCase().includes(etapa)
      );
      if (partidosEtapa.length !== 2) return;

      const todosJugados = partidosEtapa.every(
        (m) => m.score_a !== null && m.score_b !== null
      );
      if (!todosJugados) return;

      const predsEtapa = userPreds.filter((p) => {
        const match = matchesById[p.match_id];
        return match && match.phase && match.phase.toLowerCase().includes(etapa);
      });
      if (predsEtapa.length !== 2) return;

      const sumaGolesReales = partidosEtapa.reduce(
        (acc, m) => acc + (m.score_a ?? 0) + (m.score_b ?? 0),
        0
      );
      const sumaGolesPronosticados = predsEtapa.reduce(
        (acc, p) => acc + (p.predicted_score_a ?? 0) + (p.predicted_score_b ?? 0),
        0
      );

      if (sumaGolesReales === sumaGolesPronosticados && sumaGolesReales > 0) {
        items.push({
          id: `bonus-${etapa}`,
          label: `Bonus goles — ${STAGE_LABELS[etapa] ?? etapa}`,
          phase: partidosEtapa[0]?.phase,
          predictedScoreA: sumaGolesPronosticados,
          predictedScoreB: sumaGolesReales,
          actualScoreA: sumaGolesReales,
          actualScoreB: sumaGolesPronosticados,
          points: 2,
          reason: 'stage_goals_bonus',
        });
      }
    });
  }

  items.sort((a, b) => {
    if (a.date && b.date) return a.date.getTime() - b.date.getTime();
    if (a.date) return -1;
    if (b.date) return 1;
    return a.label.localeCompare(b.label);
  });

  return items;
}

function formatLigaMxPhaseLabel(phase: string): string {
  const jornadaNum = parseLigaMxJornadaNumber(phase);
  if (jornadaNum) return `Jornada ${jornadaNum}`;
  switch (phase) {
    case 'Quarters':
      return 'Cuartos de Final';
    case 'Semifinals':
      return 'Semifinales';
    case 'Final':
      return 'Final';
    default:
      return phase;
  }
}

export function computeLigaMxStageLeaderboards(
  users: { uuid: string; name: string }[],
  predictions: PredictionLike[],
  matchesById: Record<string, ScoringMatch>
): StageLeaderboard[] {
  const pointsByPhase = new Map<string, Map<string, number>>();

  users.forEach((user) => {
    const items = buildUserPointBreakdown(user.uuid, predictions, matchesById, {
      includeStageBonuses: true,
    });
    items.forEach((item) => {
      const phase = item.phase || '—';
      if (!pointsByPhase.has(phase)) pointsByPhase.set(phase, new Map());
      const userPoints = pointsByPhase.get(phase)!;
      userPoints.set(user.uuid, (userPoints.get(user.uuid) ?? 0) + item.points);
    });
  });

  const phases = Array.from(
    new Set([
      ...Object.values(matchesById)
        .filter((m) => m.score_a !== null && m.score_b !== null && m.phase)
        .map((m) => m.phase as string),
      ...Array.from(pointsByPhase.keys()),
    ])
  ).sort((a, b) => {
    const na = parseLigaMxJornadaNumber(a) ?? 999;
    const nb = parseLigaMxJornadaNumber(b) ?? 999;
    return na - nb || a.localeCompare(b);
  });

  return phases.map((phase) => {
    const phaseMatches = Object.values(matchesById).filter((m) => m.phase === phase);
    const isComplete =
      phaseMatches.length > 0 &&
      phaseMatches.every((m) => m.score_a !== null && m.score_b !== null);

    const rankings = users
      .filter((u) => u.name)
      .map((user) => ({
        uuid: user.uuid,
        name: user.name,
        points: pointsByPhase.get(phase)?.get(user.uuid) ?? 0,
      }))
      .sort((a, b) => b.points - a.points);

    return {
      id: phase,
      title: formatLigaMxPhaseLabel(phase),
      isComplete,
      rankings,
      matchFilter: (m: Match) => m.phase === phase,
    };
  });
}
